import { put, call, takeLatest } from 'redux-saga/effects';

import { getAccessToken } from '../../lib/session';
import { getMe, sendOTP, verifyOTP, googleLogin } from '../apis/auth';
import { setMFARequired, setEnrollmentRequired } from '../slices/mfa';
import {
  logout,
  setUser,
  sendOTPStart,
  sessionExpired,
  sendOTPSuccess,
  sendOTPFailure,
  verifyOTPStart,
  initializeAuth,
  verifyOTPSuccess,
  verifyOTPFailure,
  googleLoginStart,
  googleLoginSuccess,
  googleLoginFailure,
  userHydrationSettled,
} from '../slices/auth';

// Login responses: enrollment_required (set up TOTP), mfa_required (verify TOTP), or
// tokens (trusted device or non-admin app).
function* routeLoginResponse(response, fallbackSuccessAction) {
  if (response.enrollment_required && response.enrollment_token) {
    yield put(setEnrollmentRequired({ enrollmentToken: response.enrollment_token }));
    return;
  }
  if (response.mfa_required && response.mfa_token) {
    yield put(
      setMFARequired({
        mfaToken: response.mfa_token,
        recoveryCodesRemaining: response.recovery_codes_remaining,
      })
    );
    return;
  }
  yield put(fallbackSuccessAction(response));
}

function* handleSendOTP(action) {
  try {
    const response = yield call(sendOTP, action.payload.phoneNumber);
    if (response.success) {
      yield put(sendOTPSuccess(response));
    } else {
      yield put(sendOTPFailure(response.error || 'Failed to send OTP'));
    }
  } catch (error) {
    yield put(
      sendOTPFailure(error.response?.data?.error?.message || error.message || 'Failed to send OTP')
    );
  }
}

function* handleVerifyOTP(action) {
  try {
    const { phoneNumber, otpCode } = action.payload;
    const response = yield call(verifyOTP, phoneNumber, otpCode);
    if (response.success) {
      yield* routeLoginResponse(response, verifyOTPSuccess);
    } else {
      yield put(verifyOTPFailure(response.error || 'Invalid OTP'));
    }
  } catch (error) {
    yield put(
      verifyOTPFailure(
        error.response?.data?.error?.message || error.message || 'Failed to verify OTP'
      )
    );
  }
}

function* handleGoogleLogin(action) {
  try {
    const response = yield call(googleLogin, action.payload.idToken);
    if (response.success) {
      yield* routeLoginResponse(response, googleLoginSuccess);
    } else {
      yield put(googleLoginFailure(response.error || 'Google sign-in failed'));
    }
  } catch (error) {
    yield put(
      googleLoginFailure(
        error.response?.data?.error?.message || error.message || 'Google sign-in failed'
      )
    );
  }
}

// The MFA path omits user_data, so re-read `role` on every boot to keep role-gated nav right.
function* hydrateUser() {
  if (!getAccessToken()) {
    yield put(userHydrationSettled());
    return;
  }
  try {
    const me = yield call(getMe);
    if (me?.user_id) {
      const userData = { ...me };
      delete userData.success;
      yield put(setUser(userData));
    }
  } catch {
    // Keep whatever the login flow already stored; the interceptor refreshes or ends
    // a dead session, so this only swallows transport-level failures.
  } finally {
    // Settle either way, so a failed lookup can't leave a role guard pending forever.
    yield put(userHydrationSettled());
  }
}

export default function* authSaga() {
  yield takeLatest(
    [
      initializeAuth.type,
      verifyOTPSuccess.type,
      googleLoginSuccess.type,
      logout.type,
      sessionExpired.type,
    ],
    hydrateUser
  );
  yield takeLatest(sendOTPStart.type, handleSendOTP);
  yield takeLatest(verifyOTPStart.type, handleVerifyOTP);
  yield takeLatest(googleLoginStart.type, handleGoogleLogin);
}
